import "../../../mobile/tizen/src/activation.js";
import { useCallback, useEffect, useRef, useState } from "react";
import { terminalApi } from "./api";
import { terminalStorage } from "./storage";
import { useKioskStore } from "./store";
import { describeError, startupError, startupLog, type StartupFailure } from "./startupDiagnostics";
import type { BootstrapResponse, BootstrapStatusResponse, PlayerCommand, SurfaceType, TerminalConfiguration } from "./types";

declare global {
  interface Window {
    FlexTechActivationSession: (options: {
      activation: BootstrapResponse;
      poll: (token: string) => Promise<BootstrapStatusResponse>;
      renew: (token: string) => Promise<BootstrapStatusResponse>;
      onChange: (activation: BootstrapResponse, seconds: number, message: string) => void;
      onActive: (payload: { device_token: string }) => void;
      onFatal: (error: Error) => void;
    }) => { stop: () => void };
  }
}

export const PLAYER_STATES = {
  BOOTING: "booting",
  WAITING: "waiting_for_activation",
  READY: "ready",
  NEEDS_ASSIGNMENT: "needs_assignment",
  ERROR: "error"
} as const;

export type PlayerState = typeof PLAYER_STATES[keyof typeof PLAYER_STATES];

export function platformName() {
  const userAgent = window.navigator.userAgent.toLowerCase();
  if (window.Capacitor) {
    startupLog("CAPACITOR_READY", {
      plugins: Object.keys((window.Capacitor as { Plugins?: Record<string, unknown> }).Plugins || {})
    });
    return "ios_capacitor";
  }
  if (userAgent.includes("tizen")) return "samsung_tizen_sssp";
  if (userAgent.includes("clickshare")) return "clickshare";

  return "browser";
}

type PlayerRuntimeOptions = {
  platform?: string;
  capabilities?: Record<string, unknown>;
};

function platformCapabilities(overrides: Record<string, unknown> = {}) {
  return {
    touch: navigator.maxTouchPoints > 0,
    online: navigator.onLine,
    screen: {
      width: window.screen.width,
      height: window.screen.height,
      device_pixel_ratio: window.devicePixelRatio
    },
    ...overrides
  };
}

export function surfaceForConfiguration(configuration: TerminalConfiguration): SurfaceType {
  return configuration.player.surface_type || configuration.player.device_type;
}

export function isPassiveSurface(surfaceType: SurfaceType) {
  return surfaceType === "content";
}

export function usePlayerRuntime(options: PlayerRuntimeOptions = {}) {
  const [state, setState] = useState<PlayerState>(PLAYER_STATES.BOOTING);
  const [activationSeconds, setActivationSeconds] = useState(0);
  const [activationMessage, setActivationMessage] = useState("");
  const [activation, setActivation] = useState<BootstrapResponse | null>(null);
  const [deviceToken, setDeviceToken] = useState<string | null>(null);
  const [configuration, setConfiguration] = useState<TerminalConfiguration | null>(() => terminalStorage.getCachedConfiguration());
  const [error, setError] = useState<string | null>(null);
  const [startupStage, setStartupStage] = useState("APP_START");
  const [startupFailure, setStartupFailure] = useState<StartupFailure | null>(null);
  const setKioskConfiguration = useKioskStore((store) => store.setConfiguration);
  const configurationLoadRef = useRef<Promise<TerminalConfiguration> | null>(null);

  const markStartupStage = useCallback((stage: string, details?: Record<string, unknown>) => {
    setStartupStage(stage);
    startupLog(stage, details);
  }, []);

  const failStartupStage = useCallback((stage: string, err: unknown, details?: Record<string, unknown>) => {
    const failure = startupError(stage, err, details);
    setStartupStage(stage);
    setStartupFailure(failure);
    setError(failure.message);
    return failure;
  }, []);

  const loadConfiguration = useCallback((token: string) => {
    if (configurationLoadRef.current) return configurationLoadRef.current;

    const request = (async () => {
      markStartupStage("CONFIG_LOAD_START");
      const payload = await terminalApi.configuration(token);
      setConfiguration(payload);
      terminalStorage.setCachedConfiguration(payload);
      setKioskConfiguration(payload);
      setState(surfaceForConfiguration(payload) === "unconfigured" ? PLAYER_STATES.NEEDS_ASSIGNMENT : PLAYER_STATES.READY);
      markStartupStage("CONFIG_LOAD_COMPLETE", { config_version: payload.config_version });
      markStartupStage("API_READY", { surface_type: surfaceForConfiguration(payload) });
      return payload;
    })();
    const trackedRequest = request.finally(() => {
      if (configurationLoadRef.current === trackedRequest) configurationLoadRef.current = null;
    });
    configurationLoadRef.current = trackedRequest;
    return trackedRequest;
  }, [markStartupStage, setKioskConfiguration]);

  const startBootstrap = useCallback(async () => {
    setState(PLAYER_STATES.BOOTING);
    setError(null);
    setStartupFailure(null);
    markStartupStage("AUTH_START", { mode: "device_bootstrap", online: navigator.onLine });
    const payload = await terminalApi.bootstrap({
      installation_uuid: terminalStorage.getInstallationUuid(),
      platform: options.platform || platformName(),
      app_version: "1.0.0",
      capabilities: platformCapabilities(options.capabilities),
      metadata: { user_agent: window.navigator.userAgent }
    });
    setActivation(payload);
    setState(PLAYER_STATES.WAITING);
    markStartupStage("AUTH_COMPLETE", { status: "waiting_for_activation" });
  }, [markStartupStage, options.capabilities, options.platform]);

  const boot = useCallback(async () => {
    setState(PLAYER_STATES.BOOTING);
    setError(null);
    setStartupFailure(null);
    markStartupStage("STORAGE_INIT");
    const token = terminalStorage.getDeviceToken();

    if (token) {
      try {
        markStartupStage("AUTH_START", { mode: "stored_device_token", online: navigator.onLine });
        setDeviceToken(token);
        await loadConfiguration(token);
        markStartupStage("AUTH_COMPLETE", { mode: "stored_device_token" });
        return;
      } catch (err) {
        startupError("AUTH_START", err, { mode: "stored_device_token" });
        terminalStorage.clearDeviceToken();
        terminalStorage.clearCachedConfiguration();
      }
    }

    await startBootstrap();
  }, [loadConfiguration, startBootstrap]);

  useEffect(() => {
    boot().catch((err: Error) => {
      failStartupStage("APP_START", err);
      setState(PLAYER_STATES.ERROR);
    });
  }, [boot, failStartupStage]);

  useEffect(() => {
    if (state !== PLAYER_STATES.WAITING || !activation?.poll_token) return undefined;

    const session = window.FlexTechActivationSession({
      activation,
      poll: terminalApi.bootstrapStatus,
      renew: terminalApi.renewActivation,
      onChange: (payload, seconds, message) => {
        setActivation(payload);
        setActivationSeconds(seconds);
        setActivationMessage(message);
      },
      onActive: (payload) => {
        setState(PLAYER_STATES.BOOTING);
        terminalStorage.setDeviceToken(payload.device_token);
        setDeviceToken(payload.device_token);
        loadConfiguration(payload.device_token).catch((err) => {
          failStartupStage("AUTH_START", err);
          setState(PLAYER_STATES.ERROR);
        });
      },
      onFatal: (err) => {
        failStartupStage("AUTH_START", err, { mode: "activation_poll" });
        setState(PLAYER_STATES.ERROR);
      }
    });
    return () => session.stop();
  }, [activation?.poll_token, loadConfiguration, state, failStartupStage]);

  useEffect(() => {
    document.title = configuration?.tenant?.name ? `${configuration.tenant.name} Player` : "FlexTech Player";
  }, [configuration?.tenant?.name]);

  return {
    state,
    activation,
    activationSeconds,
    activationMessage,
    deviceToken,
    configuration,
    error,
    startupStage,
    startupFailure,
    boot,
    loadConfiguration
  };
}

export function usePlayerCommands(deviceToken: string | null, configuration: TerminalConfiguration | null, onRefreshConfig: () => Promise<void>) {
  const realtimeConnectedRef = useRef(false);
  const heartbeatInFlightRef = useRef(false);
  const processingCommandIdsRef = useRef(new Set<string>());
  const lastRealtimeStatusRef = useRef<{
    status: "connected" | "disconnected" | "error";
    error_message?: string;
    config_version_applied?: number;
  } | null>(null);

  useEffect(() => {
    if (!deviceToken || !configuration) return undefined;

    let stopped = false;
    const activeConfiguration = configuration;
    const realtimeEnabled = Boolean(activeConfiguration.realtime?.enabled && activeConfiguration.realtime.websocket_url);

    async function handleCommand(command: PlayerCommand) {
      if (terminalStorage.getProcessedCommandIds().includes(command.id) || processingCommandIdsRef.current.has(command.id)) return;
      processingCommandIdsRef.current.add(command.id);

      try {
        if (command.command_type === "reload" || command.command_type === "restart") {
          await terminalApi.acknowledgeCommand(deviceToken!, command.id, { status: "acknowledged" });
          terminalStorage.markCommandProcessed(command.id);
          window.location.reload();
          return;
        }

        if (command.command_type === "refresh_config") {
          await onRefreshConfig();
        }

        if (command.command_type === "disable") {
          terminalStorage.clearDeviceToken();
          terminalStorage.clearCachedConfiguration();
          await terminalApi.acknowledgeCommand(deviceToken!, command.id, { status: "acknowledged" });
          terminalStorage.markCommandProcessed(command.id);
          window.location.reload();
          return;
        }

        await terminalApi.acknowledgeCommand(deviceToken!, command.id, { status: "acknowledged" });
        terminalStorage.markCommandProcessed(command.id);
      } catch (err) {
        await terminalApi.acknowledgeCommand(deviceToken!, command.id, {
          status: "failed",
          error_message: err instanceof Error ? err.message : "Command failed"
        }).catch(() => undefined);
      } finally {
        processingCommandIdsRef.current.delete(command.id);
      }
    }

    async function heartbeatFallback() {
      if (heartbeatInFlightRef.current) return;

      try {
        heartbeatInFlightRef.current = true;
        const payload = await terminalApi.heartbeat(deviceToken!, {
          config_version_applied: activeConfiguration.config_version,
          current_content_version: activeConfiguration.config_version
        });
        if (stopped) return;
        if (payload.config_version && activeConfiguration.config_version && payload.config_version > activeConfiguration.config_version) {
          await onRefreshConfig();
        }
        for (const command of payload.commands || []) {
          await handleCommand(command);
        }
      } catch {
        // Heartbeat is best-effort from the client perspective; the next tick will retry.
      } finally {
        heartbeatInFlightRef.current = false;
      }
    }

    if (!realtimeEnabled) {
      heartbeatFallback();
    }

    const heartbeatIntervalSeconds = realtimeEnabled
      ? activeConfiguration.realtime?.heartbeat_connected_seconds || activeConfiguration.heartbeat_interval_seconds || 60
      : activeConfiguration.heartbeat_interval_seconds || 30;
    const heartbeatInterval = window.setInterval(heartbeatFallback, heartbeatIntervalSeconds * 1000);
    return () => {
      stopped = true;
      window.clearInterval(heartbeatInterval);
    };
  }, [configuration, deviceToken, onRefreshConfig]);

  useEffect(() => {
    if (!deviceToken || !configuration?.realtime?.enabled || !configuration.realtime.websocket_url) return undefined;

    let stopped = false;
    const activeConfiguration = configuration;
    const realtime = activeConfiguration.realtime!;
    let socket: WebSocket;
    try {
      socket = new WebSocket(realtime.websocket_url!, appsyncEventSubprotocols(realtime.token, realtime.http_host));
    } catch (err) {
      startupError("REALTIME_SOCKET_START", err);
      lastRealtimeStatusRef.current = {
        status: "error",
        error_message: describeError(err),
        config_version_applied: activeConfiguration.config_version
      };
      terminalApi.realtimeStatus(deviceToken!, lastRealtimeStatusRef.current).catch(() => undefined);
      return undefined;
    }
    const subscriptionIds = new Map<string, string>();

    async function markStatus(status: "connected" | "disconnected" | "error", error_message?: string) {
      realtimeConnectedRef.current = status === "connected";
      const payload = {
        status,
        error_message,
        config_version_applied: activeConfiguration.config_version
      };
      const lastStatus = lastRealtimeStatusRef.current;
      if (
        lastStatus?.status === payload.status &&
        lastStatus.error_message === payload.error_message &&
        lastStatus.config_version_applied === payload.config_version_applied
      ) {
        return;
      }

      lastRealtimeStatusRef.current = payload;
      await terminalApi.realtimeStatus(deviceToken!, payload).catch(() => undefined);
    }

    async function handleRealtimeCommand(command: PlayerCommand) {
      if (terminalStorage.getProcessedCommandIds().includes(command.id) || processingCommandIdsRef.current.has(command.id)) return;
      processingCommandIdsRef.current.add(command.id);

      try {
        if (command.command_type === "refresh_config") {
          await onRefreshConfig();
        }

        await terminalApi.acknowledgeCommand(deviceToken!, command.id, { status: "acknowledged" });
        terminalStorage.markCommandProcessed(command.id);

        if (command.command_type === "reload" || command.command_type === "restart") {
          window.location.reload();
        }

        if (command.command_type === "disable") {
          terminalStorage.clearDeviceToken();
          terminalStorage.clearCachedConfiguration();
          window.location.reload();
        }
      } catch (err) {
        await terminalApi.acknowledgeCommand(deviceToken!, command.id, {
          status: "failed",
          error_message: err instanceof Error ? err.message : "Realtime command failed"
        }).catch(() => undefined);
      } finally {
        processingCommandIdsRef.current.delete(command.id);
      }
    }

    socket.addEventListener("open", () => {
      if (stopped) return;
      socket.send(JSON.stringify({ type: "connection_init" }));
    });

    socket.addEventListener("message", (message) => {
      for (const event of extractRealtimeEvents(message.data)) {
        if (event.type === "connection_ack") {
          markStatus("connected");
          for (const channel of realtime.channels || [realtime.channel].filter(Boolean)) {
            const id = globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`;
            subscriptionIds.set(channel, id);
            socket.send(JSON.stringify({
              type: "subscribe",
              id,
              channel,
              authorization: { Authorization: realtime.token }
            }));
          }
          continue;
        }

        if (event.type === "ka" || event.type === "subscribe_success") continue;

        if (event.type === "subscribe_error" || event.type === "broadcast_error") {
          markStatus("error", JSON.stringify(event.errors || event));
          continue;
        }

        const command = commandFromRealtimeEvent(event);
        if (command) {
          handleRealtimeCommand(command);
        } else if (event.type === "refresh_config" || event.event_type === "refresh_config") {
          onRefreshConfig().catch(() => undefined);
        } else if (event.type === "reload" || event.event_type === "reload") {
          window.location.reload();
        }
      }
    });

    socket.addEventListener("close", () => {
      if (!stopped) markStatus("disconnected");
    });

    socket.addEventListener("error", () => {
      if (!stopped) markStatus("error", "Realtime socket error");
    });

    return () => {
      stopped = true;
      realtimeConnectedRef.current = false;
      for (const id of subscriptionIds.values()) {
        if (socket.readyState === WebSocket.OPEN) {
          socket.send(JSON.stringify({ type: "unsubscribe", id }));
        }
      }
      socket.close();
    };
  }, [configuration, deviceToken, onRefreshConfig]);
}

function appsyncEventSubprotocols(token: string, httpHost: string | null) {
  const authorization = {
    Authorization: token,
    ...(httpHost ? { host: httpHost } : {})
  };
  return [`header-${base64UrlEncode(JSON.stringify(authorization))}`, "aws-appsync-event-ws"];
}

function base64UrlEncode(value: string) {
  return btoa(value)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

function extractRealtimeEvents(data: unknown): Array<Record<string, unknown>> {
  const parsed = typeof data === "string" ? parseJson(data) : data;
  if (!parsed || typeof parsed !== "object") return [];
  const record = parsed as Record<string, unknown>;

  if (Array.isArray(parsed)) {
    return parsed.flatMap((event) => extractRealtimeEvents(event));
  }

  if (Array.isArray(record.events)) {
    return record.events.flatMap((event) => extractRealtimeEvents(event));
  }

  if (Array.isArray(record.event)) {
    return record.event.flatMap((event) => extractRealtimeEvents(event));
  }

  if (typeof record.event === "string") {
    return extractRealtimeEvents(record.event);
  }

  return [record];
}

function commandFromRealtimeEvent(event: Record<string, unknown>): PlayerCommand | null {
  const payload = event.payload;
  if (payload && typeof payload === "object") {
    const command = payload as Partial<PlayerCommand>;
    if (typeof command.id === "string" && typeof command.command_type === "string") {
      return {
        id: command.id,
        command_type: command.command_type as PlayerCommand["command_type"],
        payload: command.payload || {},
        issued_at: command.issued_at || null
      };
    }
  }

  if (typeof event.id === "string" && typeof event.command_type === "string") {
    return {
      id: event.id,
      command_type: event.command_type as PlayerCommand["command_type"],
      payload: typeof event.payload === "object" && event.payload ? event.payload as Record<string, unknown> : {},
      issued_at: typeof event.issued_at === "string" ? event.issued_at : null
    };
  }

  return null;
}

function parseJson(value: string) {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}
